import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import AsyncButton from 'js/components/Forms/AsyncButton';
import { InlineAlert } from 'js/components/InlineAlert/InlineAlert';
import { canManageAccount } from 'js/utils/authorization-utils';
import { allowAssessmentAdmin } from 'js/utils/entitlement-utils';
import { faCircleInfo, faTrophyAlt } from '@fortawesome/pro-regular-svg-icons';
import { RadioOption, RadioOptionGroup } from 'js/components/Forms/RadioOption';

import styles from './LaunchDraftModal.module.scss';

export const LAUNCH_CHOICES = {
  ONCE: 'once',
  UPDATE: 'update',
  NEW: 'new',
};

/**
 * Asks what should happen to a customized assessment's changes as it
 * launches: kept to this assessment, applied to the template it came from
 * (only a template the workspace owns), or saved as a new template.
 */
/**
 * Only account admins can change the plan, and only accounts billed through
 * Stripe change it themselves. Anyone else is told to ask their admin.
 */
function UpgradeAction({ account }) {
  return account.billing_type === 'stripe' ? (
    <Link className="btn btn-link" to={`/a/${account.id}/settings/billing`}>
      See plans
    </Link>
  ) : (
    <a className="btn btn-link" href="mailto:support@waypointr.com">
      Contact us
    </a>
  );
}

export default function LaunchDraftModal({
  isOpen,
  onCancel,
  onLaunch,
  teamName,
  templateName,
  canUpdateTemplate,
  templateHasUnpublishedChanges = false,
}) {
  const account = useSelector((state) => state.activeAccount);
  const me = useSelector((state) => state.identity.me);
  // Keeping changes as a template is part of the paid plans; one-off
  // changes are open to everyone
  const canSaveTemplates = allowAssessmentAdmin(account);

  const managesAccount = !!(
    me?.accounts &&
    account &&
    canManageAccount(me, account.id)
  );

  const [choice, setChoice] = useState(LAUNCH_CHOICES.ONCE);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setChoice(LAUNCH_CHOICES.ONCE);
      setNewName(`${templateName} (${teamName})`);
    }
  }, [isOpen, templateName, teamName]);

  const nameMissing = choice === LAUNCH_CHOICES.NEW && newName.trim() === '';

  const optionProps = (value, needsPlan = false) => ({
    name: 'launch-draft-choice',
    value,
    checked: choice === value,
    disabled: needsPlan && !canSaveTemplates,
    onChange: () => setChoice(value),
  });

  return (
    <Modal isOpen={isOpen} toggle={onCancel}>
      <ModalHeader toggle={onCancel} tag="div" className={styles.header}>
        What should happen to your changes?
      </ModalHeader>
      <ModalBody>
        <RadioOptionGroup>
          <RadioOption
            {...optionProps(LAUNCH_CHOICES.ONCE)}
            title="Use them for this assessment only"
            description={`${templateName} stays as it is.`}
          />
          {canUpdateTemplate && (
            <RadioOption
              {...optionProps(LAUNCH_CHOICES.UPDATE, true)}
              title={`Update ${templateName}`}
              description="Everyone who uses the template gets your changes from now on."
            >
              {templateHasUnpublishedChanges && (
                <InlineAlert icon={faCircleInfo} className="mb-0">
                  The template editor has changes that haven't been published
                  yet. Only your changes are published; where you both changed
                  the same thing, yours replace the editor's.
                </InlineAlert>
              )}
            </RadioOption>
          )}
          <RadioOption
            {...optionProps(LAUNCH_CHOICES.NEW, true)}
            title="Save them as a new template"
            description="Creates your own copy with these changes, ready to use again."
          >
            {choice === LAUNCH_CHOICES.NEW && (
              <Input
                aria-label="New template name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            )}
          </RadioOption>
        </RadioOptionGroup>
        {!canSaveTemplates && (
          <InlineAlert
            icon={faTrophyAlt}
            className="mt-4 mb-0"
            actions={
              managesAccount ? <UpgradeAction account={account} /> : null
            }
          >
            Want to reuse your changes? Paid plans let you save them as your own
            template and keep it up to date.
            {!managesAccount && ' Ask your account admin about upgrading.'}
          </InlineAlert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={onCancel}>
          Cancel
        </Button>
        <AsyncButton
          color="primary"
          disabled={nameMissing}
          onClick={() => onLaunch(choice, newName.trim())}
        >
          Launch for {teamName}
        </AsyncButton>
      </ModalFooter>
    </Modal>
  );
}
